import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Inbox as InboxIcon, Sparkles, Loader2, CalendarPlus, PenLine, User } from "lucide-react";
import { PageBody, PageHeader, EmptyState } from "@/components/Page";
import { Button } from "@/components/ui/button";
import { LogReplyDialog } from "@/components/LogReplyDialog";
import { ContactDetail } from "@/components/ContactDetail";
import { REPLY_LABEL } from "@/lib/constants";
import api from "@/lib/api";

export default function Inbox() {
  const [replies, setReplies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = async () => { const { data } = await api.get("/replies"); setReplies(data); if (data.length) setSelected((s) => data.find((r) => r.id === s?.id) || data[0]); };
  useEffect(() => { load(); }, []);

  const classify = async (r) => {
    setBusy(true);
    try { const { data } = await api.post(`/replies/${r.id}/classify`); setSelected({ ...r, ...data }); load(); toast.success(`Classified: ${data.category}`); }
    catch { toast.error("Classification failed"); } finally { setBusy(false); }
  };
  const book = async (r) => {
    try {
      const { data } = await api.post(`/replies/${r.id}/book-meeting`);
      toast.success("Meeting booked & deal advanced");
      if (data.booking_url) window.open(data.booking_url, "_blank", "noopener");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "no_booking") toast.error("No booking link set", { description: d.message, action: { label: "Set it up", onClick: () => window.location.assign("/app/settings/integrations") } });
      else toast.error("Could not book");
    }
  };

  return (
    <PageBody>
      <PageHeader eyebrow="Reply Inbox" title="Every reply, classified"
        subtitle="Interested, objection, OOO, referral, meeting - with a next-best action and a booking path."
        actions={<Button onClick={() => setLogOpen(true)} className="rounded-md bg-amber-500 text-black hover:bg-amber-400" data-testid="log-reply-btn"><PenLine className="h-4 w-4 mr-1.5" /> Log reply</Button>} />
      {replies.length === 0 ? (
        <EmptyState icon={InboxIcon} title="No replies yet" body="No mail connector? Log replies you received elsewhere - DealsOps will classify, stop the sequence, and advance the deal."
          action={<Button onClick={() => setLogOpen(true)} className="rounded-md bg-amber-500 text-black hover:bg-amber-400" data-testid="log-reply-empty-btn"><PenLine className="h-4 w-4 mr-1.5" /> Log a reply</Button>} />
      ) : (
        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 space-y-2">
            {replies.map((r) => {
              const meta = REPLY_LABEL[r.category] || {};
              return (
                <button key={r.id} onClick={() => setSelected(r)} data-testid={`inbox-reply-item`}
                  className={`w-full text-left rounded-lg border p-4 transition-colors ${selected?.id === r.id ? "border-amber-500/60 bg-card" : "border-border/70 bg-card/40 hover:border-border"}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-medium text-sm truncate">{r.contact_name}</span>
                    {r.category && <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${meta.color}`} data-testid="reply-classify-badge">{meta.label}</span>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{r.contact_company}</div>
                  <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{r.body}</p>
                </button>
              );
            })}
          </div>
          <div className="lg:col-span-3">
            {selected && (
              <div className="rounded-xl border border-border/70 bg-card/50 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="font-medium">{selected.contact_name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{selected.contact_email}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {selected.contact_id && <Button size="sm" variant="ghost" onClick={() => setDetailId(selected.contact_id)} data-testid="inbox-open-contact"><User className="h-4 w-4 mr-1.5" /> Open contact</Button>}
                    <Button size="sm" variant="outline" onClick={() => classify(selected)} disabled={busy} data-testid="classify-btn">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} Classify
                    </Button>
                  </div>
                </div>
                <div className="rounded-lg bg-accent/40 border border-border/50 p-4 text-sm leading-relaxed whitespace-pre-wrap">{selected.body}</div>
                {selected.category && (
                  <div className="mt-4 space-y-3">
                    <div className="flex items-center gap-2 text-xs">
                      <span className={`font-mono uppercase px-2 py-0.5 rounded ${(REPLY_LABEL[selected.category] || {}).color}`}>{(REPLY_LABEL[selected.category] || {}).label}</span>
                      {selected.confidence != null && <span className="text-muted-foreground">{selected.confidence}% confidence</span>}
                    </div>
                    {selected.summary && <p className="text-sm text-muted-foreground">{selected.summary}</p>}
                    {selected.next_action && (
                      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2" data-testid="reply-next-action">
                        <Sparkles className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                        <div><span className="text-xs font-mono uppercase tracking-wider text-amber-500">Next best action</span><p className="text-sm mt-0.5">{selected.next_action}</p></div>
                      </div>
                    )}
                    {selected.suggested_reply && (
                      <div className="rounded-lg border border-border/60 p-4">
                        <div className="eyebrow mb-2">Suggested reply</div>
                        <p className="text-sm">{selected.suggested_reply}</p>
                      </div>
                    )}
                    {(selected.category === "meeting" || selected.category === "interested") && (
                      <Button onClick={() => book(selected)} className="rounded-md bg-amber-500 text-black hover:bg-amber-400" data-testid="book-meeting-btn"><CalendarPlus className="h-4 w-4 mr-1.5" /> Book meeting</Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <LogReplyDialog open={logOpen} onOpenChange={setLogOpen} onSaved={(r) => { load(); setSelected(r); }} />
      <ContactDetail contactId={detailId} open={!!detailId} onOpenChange={(v) => !v && setDetailId(null)} />
    </PageBody>
  );
}
